/** FrequencyPicker — preset-only recurrence editor for scheduled tasks.
 *
 * Product constraint (contract §3/§8.3): no cron expression input anywhere.
 * Five presets map 1:1 to the backend `ScheduledTaskFrequency` enum; each
 * preset exposes only the fields backend §4.4 validation requires for it.
 * The preview panel reuses the shared `describeFrequency()` text mirror and
 * additionally estimates the first firing time client-side for immediate
 * feedback — the server remains the source of truth for the persisted
 * `nextRunAt` once the task is saved. */

import { InputNumber, Segmented, Select, Space, TimePicker, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  SCHEDULED_TASK_FREQUENCY_OPTIONS,
  WEEKDAY_OPTIONS,
  describeFrequency,
  type ScheduledTaskFrequency,
  type ScheduledTaskWeekday,
} from "../../types/scheduledTask";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;

export const DEFAULT_TIMEZONE = "Asia/Shanghai";

export interface FrequencyValue {
  frequency: ScheduledTaskFrequency;
  atHour: number | null;
  atMinute: number | null;
  byWeekday: ScheduledTaskWeekday | null;
  byMonthday: number | null;
  intervalMinutes: number | null;
  timezone: string;
}

export const DEFAULT_FREQUENCY_VALUE: FrequencyValue = {
  frequency: "daily",
  atHour: 9,
  atMinute: 0,
  byWeekday: null,
  byMonthday: null,
  intervalMinutes: null,
  timezone: DEFAULT_TIMEZONE,
};

/** Sensible per-preset defaults so switching presets always leaves the
 * required fields for §4.4 validation already filled in. `once` carries no
 * time fields at all (single fire, never rescheduled). */
function defaultsForFrequency(frequency: ScheduledTaskFrequency, tz: string): FrequencyValue {
  switch (frequency) {
    case "once":
      return { frequency, atHour: null, atMinute: null, byWeekday: null, byMonthday: null, intervalMinutes: null, timezone: tz };
    case "hourly":
      return { frequency, atHour: null, atMinute: 0, byWeekday: null, byMonthday: null, intervalMinutes: null, timezone: tz };
    case "weekly":
      return { frequency, atHour: 9, atMinute: 0, byWeekday: 0, byMonthday: null, intervalMinutes: null, timezone: tz };
    case "monthly":
      return { frequency, atHour: 9, atMinute: 0, byWeekday: null, byMonthday: 1, intervalMinutes: null, timezone: tz };
    case "custom_interval":
      return { frequency, atHour: null, atMinute: null, byWeekday: null, byMonthday: null, intervalMinutes: 60, timezone: tz };
    case "daily":
    default:
      return { frequency: "daily", atHour: 9, atMinute: 0, byWeekday: null, byMonthday: null, intervalMinutes: null, timezone: tz };
  }
}

/** Client-side estimate of the first firing time, for the live preview only. */
function estimateNextRun(value: FrequencyValue): Dayjs | null {
  const tz = value.timezone || DEFAULT_TIMEZONE;
  const now = dayjs().tz(tz);
  switch (value.frequency) {
    case "hourly": {
      if (value.atMinute == null) return null;
      let next = now.minute(value.atMinute).second(0).millisecond(0);
      if (!next.isAfter(now)) next = next.add(1, "hour");
      return next;
    }
    case "daily": {
      if (value.atHour == null || value.atMinute == null) return null;
      let next = now.hour(value.atHour).minute(value.atMinute).second(0).millisecond(0);
      if (!next.isAfter(now)) next = next.add(1, "day");
      return next;
    }
    case "weekly": {
      if (value.atHour == null || value.atMinute == null || value.byWeekday == null) return null;
      // byWeekday: 0=周一…6=周日; dayjs .day(): 0=周日…6=周六.
      const targetDow = (value.byWeekday + 1) % 7;
      let next = now.day(targetDow).hour(value.atHour).minute(value.atMinute).second(0).millisecond(0);
      if (!next.isAfter(now)) next = next.add(7, "day");
      return next;
    }
    case "monthly": {
      if (value.atHour == null || value.atMinute == null || value.byMonthday == null) return null;
      let next = now.date(value.byMonthday).hour(value.atHour).minute(value.atMinute).second(0).millisecond(0);
      if (!next.isAfter(now)) next = next.add(1, "month");
      return next;
    }
    case "custom_interval":
      return value.intervalMinutes == null ? null : now.add(value.intervalMinutes, "minute");
    default:
      return null;
  }
}

interface FrequencyPickerProps {
  value: FrequencyValue;
  onChange: (next: FrequencyValue) => void;
  /**
   * 可选的频率预设白名单（默认全部）。AI 任务（agent_run 目标）限定
   * 单次/每天/每周/每月——hourly 与 custom_interval 不对其开放。
   */
  frequencies?: ScheduledTaskFrequency[];
}

export default function FrequencyPicker({ value, onChange, frequencies }: FrequencyPickerProps) {
  const timeValue =
    value.atHour != null && value.atMinute != null
      ? dayjs().hour(value.atHour).minute(value.atMinute)
      : null;
  const preview = describeFrequency(value);
  const nextRun = estimateNextRun(value);
  const presetOptions = (
    frequencies
      ? SCHEDULED_TASK_FREQUENCY_OPTIONS.filter((o) => frequencies.includes(o.value))
      : SCHEDULED_TASK_FREQUENCY_OPTIONS
  ).map((o) => ({ value: o.value, label: o.label }));

  return (
    <div data-testid="scheduled-task-frequency-picker">
      <Segmented
        block
        value={value.frequency}
        onChange={(v) => onChange(defaultsForFrequency(v as ScheduledTaskFrequency, value.timezone))}
        options={presetOptions}
      />

      <div style={{ marginTop: "var(--jerry-sp-3)" }}>
        {value.frequency === "hourly" && (
          <Space align="center">
            <Text>每小时第</Text>
            <InputNumber
              min={0}
              max={59}
              value={value.atMinute ?? undefined}
              onChange={(v) => onChange({ ...value, atMinute: v ?? 0 })}
            />
            <Text>分钟执行</Text>
          </Space>
        )}

        {(value.frequency === "daily" || value.frequency === "weekly" || value.frequency === "monthly") && (
          <Space align="center" wrap>
            {value.frequency === "weekly" && (
              <Select<ScheduledTaskWeekday>
                style={{ width: 100 }}
                options={WEEKDAY_OPTIONS}
                value={value.byWeekday ?? undefined}
                onChange={(v) => onChange({ ...value, byWeekday: v })}
              />
            )}
            {value.frequency === "monthly" && (
              <InputNumber
                min={1}
                max={28}
                addonAfter="日"
                value={value.byMonthday ?? undefined}
                onChange={(v) => onChange({ ...value, byMonthday: v ?? 1 })}
              />
            )}
            <TimePicker
              format="HH:mm"
              minuteStep={5}
              allowClear={false}
              value={timeValue}
              onChange={(t) =>
                onChange({ ...value, atHour: t ? t.hour() : null, atMinute: t ? t.minute() : null })
              }
            />
          </Space>
        )}

        {value.frequency === "custom_interval" && (
          <Space align="center">
            <Text>每</Text>
            <InputNumber
              min={15}
              step={15}
              value={value.intervalMinutes ?? undefined}
              onChange={(v) => onChange({ ...value, intervalMinutes: Math.max(15, v ?? 15) })}
            />
            <Text>分钟执行一次（最短间隔 15 分钟）</Text>
          </Space>
        )}
      </div>

      {/* 单次没有「下次执行」概念（触发后不重臂），隐藏首触预览区。 */}
      {value.frequency !== "once" && (
      <div
        style={{
          marginTop: "var(--jerry-sp-3)",
          padding: "var(--jerry-sp-3)",
          background: "var(--jerry-gray-100)",
          borderRadius: 6,
        }}
      >
        <Text type="secondary">下次执行时间预览</Text>
        <div style={{ marginTop: 4 }}>
          {preview ? (
            <>
              <Text strong>{preview}</Text>
              {nextRun && (
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  （预计 {nextRun.format("YYYY-MM-DD HH:mm")} 首次触发）
                </Text>
              )}
            </>
          ) : (
            <Text type="secondary">请完善上方频率设置</Text>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
